// React Query hooks for CMS API

import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { cmsApi } from "@/services/cmsApi";
import type {
  CMSProductsResponse,
  CMSProductDetails,
  CMSCategory,
  CMSSiteSettings,
  CMSReviewsResponse,
  CMSSearchResult,
  CMSQuickAction,
  CMSBrand,
} from "@/types/cms";

// Cache times in milliseconds
const CACHE_TIMES = {
  taxonomy: { staleTime: 10 * 60 * 1000, gcTime: 30 * 60 * 1000 }, // 10 min stale, 30 min gc
  products: { staleTime: 5 * 60 * 1000, gcTime: 15 * 60 * 1000 },  // 5 min stale, 15 min gc
  siteSettings: { staleTime: 15 * 60 * 1000, gcTime: 30 * 60 * 1000 }, // 15 min stale, 30 min gc
  search: { staleTime: 2 * 60 * 1000, gcTime: 5 * 60 * 1000 },     // 2 min stale, 5 min gc
};

// Supplements (aliased as useProducts/useProduct for backward compatibility)
export function useProducts(params?: {
  page?: number;
  limit?: number;
  category?: string;
  search?: string;
}): UseQueryResult<CMSProductsResponse> {
  return useQuery({
    queryKey: ['cms-products', params],
    queryFn: () => cmsApi.getSupplements(params),
    ...CACHE_TIMES.products,
  });
}

export function useProduct(slug: string): UseQueryResult<CMSProductDetails> {
  return useQuery({
    queryKey: ['cms-product', slug],
    queryFn: () => cmsApi.getSupplement(slug),
    enabled: !!slug,
    ...CACHE_TIMES.products,
  });
}

export function useProductSearch(query: string): UseQueryResult<CMSSearchResult> {
  return useQuery({
    queryKey: ['cms-product-search', query],
    queryFn: () => cmsApi.searchProducts(query),
    enabled: query.length > 0,
    ...CACHE_TIMES.search,
  });
}

// Taxonomy
export function useCategories(): UseQueryResult<CMSCategory[]> {
  return useQuery({
    queryKey: ['cms-categories'],
    queryFn: async () => {
      const response = await cmsApi.getCategories();
      return response.categories;
    },
    ...CACHE_TIMES.taxonomy,
  });
}

// Reviews
export function useProductReviews(
  productId: string,
  limit?: number
): UseQueryResult<CMSReviewsResponse> {
  return useQuery({
    queryKey: ['cms-reviews', productId, limit],
    queryFn: () => cmsApi.getReviews(productId, limit),
    enabled: !!productId,
    ...CACHE_TIMES.products,
  });
}

// Site Settings
export function useCMSSiteSettings(): UseQueryResult<CMSSiteSettings> {
  return useQuery({
    queryKey: ['cms-site-settings'],
    queryFn: async () => {
      const response = await cmsApi.getSiteSettings();
      return response.settings;
    },
    ...CACHE_TIMES.siteSettings,
  });
}

// Supplements by IDs (aliased as useProductsByIds for backward compatibility)
export function useProductsByIds(ids: string[]): UseQueryResult<CMSProductsResponse> {
  return useQuery({
    queryKey: ['cms-products-by-ids', ids],
    queryFn: () => cmsApi.getSupplementsByIds(ids),
    enabled: ids.length > 0,
    ...CACHE_TIMES.products,
  });
}

// Quick Actions
export function useQuickActions(): UseQueryResult<CMSQuickAction[]> {
  return useQuery({
    queryKey: ['cms-quick-actions'],
    queryFn: async () => {
      const response = await cmsApi.getQuickActions();
      return response.quickActions;
    },
    ...CACHE_TIMES.taxonomy,
  });
}

// Brands - derived from products since CMS doesn't have a separate brands endpoint
export function useBrands(): UseQueryResult<CMSBrand[]> {
  const { data: productsData, isLoading, error } = useProducts({ limit: 1000 });
  
  return useQuery({
    queryKey: ['cms-brands-derived'],
    queryFn: async () => {
      // Derive unique brands from products
      const brandMap = new Map<string, CMSBrand>();
      
      productsData?.products?.forEach(product => {
        if (product.brand_name && product.brand_id && !brandMap.has(product.brand_id)) {
          brandMap.set(product.brand_id, {
            id: product.brand_id,
            name: product.brand_name,
            slug: product.brand_id, // Use brand_id as slug
            description: null,
            logo_url: product.brand_logo_url || null,
            website_url: null,
            is_active: true,
            display_order: 0,
            meta_title: null,
            meta_description: null,
          });
        }
      });
      
      return Array.from(brandMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    },
    enabled: !!productsData?.products && productsData.products.length > 0,
    ...CACHE_TIMES.taxonomy,
  });
}
